import type { ReactElement } from 'react';
import { Children, cloneElement, isValidElement, useEffect, useId, useRef } from 'react';
import { cx } from 'classix';

import type { LegacyIconProps } from './types';

import styles from './LegacyIcon.module.css';

/**
 * @deprecated Use `@launchpad-ui/icons` instead.
 */
const LegacyIcon = ({
  name,
  subtle,
  className,
  size,
  children,
  'data-test-id': testId = 'icon',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-hidden': ariaHidden,
  ...props
}: LegacyIconProps) => {
  const sizeClass = size ? styles[size] : false;
  const classes = cx(styles.icon, sizeClass, subtle && styles.subtle, `icon-${name}`, className);

  const containerRef = useRef<HTMLSpanElement>(null);
  const prefix = `svg-${useId()}`;

  useEffect(() => {
    const svg = containerRef.current?.querySelector('svg');
    const title = svg?.querySelector('title');
    const desc = svg?.querySelector('desc');

    if (title) {
      const id = `${prefix}-${name}-title`;
      title.setAttribute('id', id);
      svg?.setAttribute('aria-labelledby', id);
    }
    if (desc) {
      const id = `${prefix}-${name}-description`;
      desc.setAttribute('id', id);
      svg?.setAttribute('aria-describedby', id);
    }
  }, [name, prefix]);

  const isAriaHidden = ariaHidden ?? (!ariaLabelledBy && !ariaLabel);

  return (
    <span ref={containerRef} data-test-id={testId} className={classes}>
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child as ReactElement, {
            // @ts-expect-error React 19 related
            'aria-hidden': isAriaHidden,
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            ...props,
          });
        }
        return null;
      })}
    </span>
  );
};

export { LegacyIcon };
